import { Link } from "react-router-dom";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { IMAGES, FAQ_BALAYAGE } from "@/data/content";

const ABLAUF = [
  { nr: "01", title: "Beratung", text: "Wir besprechen Ausgangsfarbe, Wunsch-Look und Pflege – ausführlich und ehrlich." },
  { nr: "02", title: "Toupier-Technik", text: "Die Strähnen werden zuerst leicht toupiert, freihand aufgetragen und in Folien gewickelt – für besonders weiche Übergänge." },
  { nr: "03", title: "Glossing & Pflege", text: "Ein Glanz-Finish versiegelt die Nuance und gibt seidigen Schimmer." },
  { nr: "04", title: "Styling & Tipps", text: "Wir stylen das Ergebnis und geben Pflege-Tipps für zu Hause mit." },
];

const PFLEGE = [
  "Farbschonendes Shampoo verwenden",
  "Einmal pro Woche eine Feuchtigkeitskur",
  "Hitzeschutz vor Föhnen und Styling",
  "Glossing zwischendurch frischt den Glanz auf",
];

export default function Balayage() {
  return (
    <div data-testid="page-balayage">
      {/* HERO */}
      <section className="py-16 md:py-24 border-b border-[#24211D]/10">
        <div className="max-w-6xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <span className="text-xs uppercase tracking-[0.2em] text-[#B08D57] block mb-2 font-sans">Farbkunst in Glessen</span>
            <h1 className="text-[clamp(36px,4.5vw,72px)] leading-none mb-6">
              <span className="ln"><span>Balayage, die natürlich</span></span>
              <span className="ln"><span>herauswächst.</span></span>
            </h1>
            <p className="text-base text-[#24211D]/80 max-w-lg mb-8 leading-relaxed">
              Ob kühles Aschblond, warmes Haselnuss oder samtiges Karamell: Wir kreieren weiche Übergänge ohne Ansatzstress, die Ihr Haar monatelang natürlich strahlen lassen – mit ausführlicher Beratung und oft kurzfristig freien Terminen.
            </p>
            <Link to="/kontakt?service=Balayage#termin" data-testid="balayage-cta-btn" className="bg-[#B08D57] hover:bg-[#967442] text-[#FBF8F3] px-8 py-4 text-xs uppercase tracking-[0.18em] font-sans transition-colors duration-300 inline-block">
              Beratungstermin buchen
            </Link>
          </div>
          <div className="lg:col-span-5">
            <figure className="aspect-[3/4] relative border border-[#24211D]/10 overflow-hidden nes-wipe">
              <img src={IMAGES.balayageReal} alt="Echte Balayage aus dem Haarstudio Nes: weiche, karamellfarbene Verläufe" className="absolute inset-0 w-full h-full object-cover" />
              <figcaption className="absolute bottom-4 left-4 text-xs uppercase tracking-[0.14em] text-[#24211D]/85 bg-[#FBF8F3]/90 px-3 py-2 font-sans">Caramel Touch Glessen</figcaption>
            </figure>
          </div>
        </div>
      </section>

      {/* WAS IST BALAYAGE */}
      <section className="py-16 border-b border-[#24211D]/10">
        <div className="max-w-6xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div>
            <span className="text-xs uppercase tracking-[0.2em] text-[#B08D57] block mb-2 font-sans">Gut zu wissen</span>
            <h2 className="text-3xl md:text-4xl mb-5">Was ist Balayage?</h2>
            <p className="text-sm text-[#24211D]/80 leading-relaxed mb-4">
              Balayage ist eine Freihand-Färbetechnik: Die Farbe wird sanft auf das Haar aufgetragen und in Folien gewickelt. Bei uns arbeiten wir mit der Toupier-Technik – die Strähnen werden vorher leicht toupiert, damit sich die Farbe noch weicher ins eigene Haar legt. So entstehen natürliche Verläufe – wie von der Sonne geküsst.
            </p>
            <p className="text-sm text-[#24211D]/80 leading-relaxed">
              Der größte Vorteil: Kein harter Ansatz. Die Farbe wächst natürlich heraus und sieht auch nach Wochen noch gepflegt aus.
            </p>
          </div>
          <div>
            <span className="text-xs uppercase tracking-[0.2em] text-[#B08D57] block mb-2 font-sans">Passend für Sie?</span>
            <h2 className="text-3xl md:text-4xl mb-5">Für wen geeignet?</h2>
            <p className="text-sm text-[#24211D]/80 leading-relaxed mb-4">
              Balayage passt zu fast allen Haartypen und -längen – besonders, wenn Sie einen natürlichen Look ohne ständiges Nachfärben möchten.
            </p>
            <div className="grid grid-cols-2 gap-6 text-xs font-sans">
              <div className="border-l border-[#B08D57] pl-4">
                <span className="font-bold block text-[#24211D]">Schonendes Aufhellen</span>
                <span className="text-[#24211D]/70">Schutz der Haarstruktur</span>
              </div>
              <div className="border-l border-[#B08D57] pl-4">
                <span className="font-bold block text-[#24211D]">Inklusive Glossing</span>
                <span className="text-[#24211D]/70">Für maximalen Seidenglanz</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ABLAUF & DAUER */}
      <section className="bg-[#24211D] text-[#FBF8F3] py-20">
        <div className="max-w-6xl mx-auto px-6">
          <div className="mb-14">
            <span className="text-xs uppercase tracking-[0.2em] text-[#B08D57] block mb-2 font-sans">Der Ablauf</span>
            <h2 className="text-3xl md:text-4xl">So läuft Ihr Balayage-Termin ab</h2>
            <p className="text-xs text-[#FBF8F3]/70 font-sans mt-3">Dauer: ca. 2–4 Stunden, je nach Haarlänge und Wunsch-Ergebnis. Richtpreis: ab 120 € – genau nach Beratung.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {ABLAUF.map((s) => (
              <div key={s.nr} className="border-t border-[#B08D57]/40 pt-6" data-testid={`ablauf-${s.nr}`}>
                <span className="text-xs font-display text-[#B08D57] block mb-2">{s.nr}</span>
                <h3 className="text-xl mb-2">{s.title}</h3>
                <p className="text-xs text-[#FBF8F3]/70 leading-relaxed font-sans">{s.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PFLEGE-TIPPS + VORHER/NACHHER */}
      <section className="py-20 border-b border-[#24211D]/10">
        <div className="max-w-6xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <span className="text-xs uppercase tracking-[0.2em] text-[#B08D57] block mb-2 font-sans">Pflege-Tipps</span>
            <h2 className="text-3xl md:text-4xl mb-6">So bleibt die Nuance frisch</h2>
            <ul className="space-y-3 mb-8">
              {PFLEGE.map((t) => (
                <li key={t} className="text-sm text-[#24211D]/80 border-b border-[#24211D]/10 pb-3 flex gap-3">
                  <span className="text-[#B08D57]">—</span>{t}
                </li>
              ))}
            </ul>
            <p className="text-xs text-[#24211D]/60 font-sans">Bei Ihrem Termin erhalten Sie eine persönliche Pflege-Empfehlung für Ihren Haartyp.</p>
          </div>
          <div className="lg:col-span-5 grid grid-cols-2 gap-4">
            <figure className="aspect-[3/4] border border-[#24211D]/10 overflow-hidden">
              <img src={IMAGES.highlightsReal} alt="Echtes Ergebnis aus dem Studio: feine Highlights" className="w-full h-full object-cover" loading="lazy" />
              <figcaption className="sr-only">Ergebnis Highlights</figcaption>
            </figure>
            <figure className="aspect-[3/4] border border-[#24211D]/10 overflow-hidden mt-8">
              <img src={IMAGES.wavesReal} alt="Echtes Ergebnis aus dem Studio: Schnitt mit weichen Wellen" className="w-full h-full object-cover" loading="lazy" />
              <figcaption className="sr-only">Ergebnis Wellen</figcaption>
            </figure>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 border-b border-[#24211D]/10">
        <div className="max-w-3xl mx-auto px-6">
          <div className="text-center mb-12">
            <span className="text-xs uppercase tracking-[0.2em] text-[#B08D57] block mb-2 font-sans">Häufige Fragen</span>
            <h2 className="text-3xl md:text-4xl">Balayage FAQ</h2>
          </div>
          <Accordion type="single" collapsible className="border-t border-[#24211D]/10" data-testid="balayage-faq">
            {FAQ_BALAYAGE.map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="border-b border-[#24211D]/10">
                <AccordionTrigger data-testid={`faq-trigger-${i}`} className="text-left font-display text-lg hover:no-underline hover:text-[#B08D57] transition-colors duration-200">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-sm text-[#24211D]/75 leading-relaxed font-sans">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#B08D57] text-[#FBF8F3] py-14">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-2xl md:text-4xl mb-3">Bereit für Ihre Wunsch-Nuance?</h2>
          <p className="text-sm text-[#FBF8F3]/85 font-sans mb-8">Kostenlose Beratung, ehrliche Empfehlung, oft kurzfristig ein Termin frei.</p>
          <Link to="/kontakt?service=Balayage#termin" data-testid="balayage-footer-cta" className="bg-[#FBF8F3] text-[#24211D] hover:bg-[#FFFFFF] px-10 py-4 text-xs uppercase tracking-[0.2em] font-sans transition-colors duration-300 inline-block">
            Beratungstermin buchen
          </Link>
        </div>
      </section>
    </div>
  );
}
